import React from 'react'
import { OodleRum } from '@oodle-ai/rum'
import { createRoot } from 'react-dom/client'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { mockServerEnabled, segmentID } from './common/utils/constants'
import { errorLogger as newrelicErrLogger } from './common/utils/ErrorHandler'
import { setupDynamicImportErrorHandler } from './common/utils/dynamicImportRetry'
import './common/utils/globalValidations'
import { SetupWorker } from 'msw/browser'

declare global {
  interface Window {
    stripe: unknown
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Calendly: any
    segmentScriptRun: (writeKey: string, environment: string) => void
    LABRA_JWT?: string
  }
}
// NewRelic initialization removed - add your new error reporting service here

const environment = import.meta.env.VITE_ENVIRONMENT ?? ''

const rumInstanceId = import.meta.env.VITE_OODLE_RUM_INSTANCE_ID ?? ''
const rumApiKey = import.meta.env.VITE_OODLE_RUM_API_KEY ?? ''
const rumEndpoint = import.meta.env.VITE_OODLE_RUM_ENDPOINT ?? ''

const rumConfigured = Boolean(rumInstanceId && rumApiKey && rumEndpoint)
const isProduction =
  ['production', 'prd'].includes(environment) && import.meta.env.PROD

if (rumConfigured) {
  try {
    OodleRum.init({
      instanceId: rumInstanceId,
      apiKey: rumApiKey,
      endpoint: rumEndpoint,
      service:
        import.meta.env.VITE_OTEL_SERVICE_NAME ??
        `${environment || 'dev'}-webapp-frontend`,
      env: environment,
      sessionSampleRate: isProduction ? 100 : 0,
      replaySampleRate: isProduction ? 100 : 0,
      sessionReplay: isProduction,
      privacyLevel: 'mask-user-input',
      openTelemetry: { enabled: isProduction },
      // Omitting this defaults to /.*/, which would attach `traceparent` to every
      // cross-origin call (Segment, Auth0, Hotjar) and fail their CORS preflight.
      allowedTracingUrls: [/^https:\/\/([a-z0-9-]+\.)?api\.labra\.io\//],
    })
  } catch (error) {
    console.warn('[rum] initialisation failed; continuing without RUM', error)
  }
} else {
  console.warn('[rum] VITE_OODLE_RUM_* not set; continuing without RUM')
}

if (!isProduction && mockServerEnabled) {
  import('./mocks/browser')
    .then(({ getWorker }) => getWorker())
    .then((worker: SetupWorker) => {
      worker.start()
    })
    .catch((e: Error) => {
      console.error(e)
      newrelicErrLogger(e as Error, {
        message: e,
      })
    })
}

// Global error handler for ResizeObserver loop errors
window.addEventListener('error', event => {
  if (
    event.message ===
    'ResizeObserver loop completed with undelivered notifications.'
  ) {
    // Prevent this specific error from being logged to error reporting
    event.preventDefault()
    event.stopPropagation()
    return false
  }
})

// Setup enhanced dynamic import error handling
setupDynamicImportErrorHandler()

// Handle unhandled promise rejections that might contain ResizeObserver errors
window.addEventListener('unhandledrejection', event => {
  if (event.reason?.message?.includes('ResizeObserver loop')) {
    // Prevent this specific error from being logged to error reporting
    event.preventDefault()
    return false
  }
})

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
